import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  FaFileUpload,
  FaDownload,
  FaMapMarkedAlt,
  FaBoxes,
  FaBalanceScale,
  FaInfo
} from 'react-icons/fa';
import { MdCheckCircle, MdRadioButtonUnchecked } from 'react-icons/md';
import { GetApp, Description, Clear, CheckCircle, CloudUpload } from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL, { API_ENDPOINTS, apiCall } from '../config/api';
import { useActivityLog } from '../context/ActivityLogContext';
import { formatLogTime } from '../utils/dateUtils';
import FavoriteButton from '../components/FavoriteButton';
import RelatedToolsBar from '../components/RelatedToolsBar';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';

// Cross-link the warehouse-aware sibling tool so users who land here can
// jump straight to the per-warehouse split if that's what they actually
// need (and vice versa from PODistributionWHS).
const PO_SIBLING_TOOLS = [
  {
    name:    'PO Distribution by Warehouse',
    path:    '/dashboard/po-distribution-whs',
    accent:  '#0E9F6E',
    Icon:    WarehouseRoundedIcon,
    blurb:   'Splits PO quantities across warehouses using historical sales share and current FUTQ levels — smarter allocation when you have per-warehouse stock data.',
    ctaTag:  'Distribute by warehouse',
  },
];

const PODistribution = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const fileInputRef = useRef(null);
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog: contextAddLog,
    completeActivityProcess,
  } = useActivityLog();

  const [formData, setFormData] = useState({
    productType: '', // 'rugs' or 'nonRugs'
    evenDistribution: false,
    evenDistribution4: false,
    splitWC: false, // treat L60 and L65 as separate regions
    regions: {
      NE: false,  // L10, L21, L28, L29, L40, L41
      SE: false,  // L35, L36, L37
      MW: false,  // L55, L56, L57
      WC: false,  // L60, L65
      SW: false   // L70
    }
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);

  const activityProcessIdRef = useRef(null);

  // Helper function to add logs to activity context
  const addLog = (processId, message) => {
    const formattedMessage = `[${formatLogTime()}] ${message}`;
    contextAddLog(processId, formattedMessage);
  };

  const regionInfo = {
    NE: { label: 'NE - L10, L21, L28, L29, L40, L41', locations: ['L10', 'L21', 'L28', 'L29', 'L40', 'L41'] },
    SE: { label: 'SE - L35, L36, L37', locations: ['L35', 'L36', 'L37'] },
    MW: { label: 'MW - L55, L56, L57', locations: ['L55', 'L56', 'L57'] },
    WC: { label: 'WC - L60, L65', locations: ['L60', 'L65'] },
    SW: { label: 'SW - L70', locations: ['L70'] }
  };

  const handleRegionChange = (region) => {
    setFormData(prev => ({
      ...prev,
      regions: {
        ...prev.regions,
        [region]: !prev.regions[region]
      }
    }));
  };

  const handleProductTypeChange = (event) => {
    setFormData(prev => ({
      ...prev,
      productType: event.target.value
    }));
  };

  const handleEvenDistributionChange = (event) => {
    setFormData(prev => ({
      ...prev,
      evenDistribution: event.target.checked,
    }));
  };

  const handleEvenDistribution4Change = (event) => {
    setFormData(prev => ({
      ...prev,
      evenDistribution4: event.target.checked,
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select an Excel (.xlsx) or CSV file');
        setSelectedFile(null);
      }
    }
  };

  const handleDownloadSample = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/po-distribution/download-sample`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      let sampleFileName = `PO_Distribution_Template_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) sampleFileName = match[1].replace(/['"]/g, '');
      }
      link.setAttribute('download', sampleFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Sample file downloaded successfully');
    } catch (error) {
      setError('Failed to download sample file');
      console.error('Download error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessFile = async () => {
    // Validation
    if (!formData.productType) {
      setError('Please select either Rugs or Non Rugs');
      return;
    }

    const selectedRegionsList = Object.entries(formData.regions).filter(([_, v]) => v).map(([k]) => k);
    if (selectedRegionsList.length === 0) {
      setError('Please select at least one region');
      return;
    }

    if (!selectedFile) {
      setError('Please select a file to process');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    // Generate a unique process ID for activity log
    const activityProcessId = `po-distribution-${Date.now()}`;
    activityProcessIdRef.current = activityProcessId;

    // Add to global activity log
    addActivityProcess(activityProcessId, `PO Distribution (${formData.productType})`, 'PO Distribution');
    addLog(activityProcessId, `Starting PO distribution processing...`);
    addLog(activityProcessId, `Product Type: ${formData.productType}`);
    addLog(activityProcessId, `Even Distribution: ${formData.evenDistribution}`);
    addLog(activityProcessId, `Minimum 4 Per Location: ${formData.evenDistribution4}`);
    addLog(activityProcessId, `Selected Regions: ${selectedRegionsList.join(', ')}`);
    addLog(activityProcessId, `File: ${selectedFile.name}`);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', selectedFile);
      formDataToSend.append('productType', formData.productType);
      formDataToSend.append('evenDistribution', formData.evenDistribution);
      formDataToSend.append('evenDistribution4', formData.evenDistribution4);
      formDataToSend.append('splitWC', formData.splitWC);
      formDataToSend.append('regions', JSON.stringify(formData.regions));

      addLog(activityProcessId, `⏳ Uploading and processing file...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 30 });
      setProgress(30);

      // Send file and get processed Excel back directly
      const response = await fetch(API_ENDPOINTS.PO_DISTRIBUTION_PROCESS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to process file');
      }

      setProgress(80);
      addLog(activityProcessId, `✓ Processing complete, downloading...`);

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'PO_Distribution_Results.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          fileName = match[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setProgress(100);
      addLog(activityProcessId, `✓ Excel downloaded: ${fileName}`);
      addLog(activityProcessId, `✓ Processing completed successfully!`);
      setSuccess('File processed and downloaded successfully');
      completeActivityProcess(activityProcessId, true);
    } catch (error) {
      console.error('Processing error:', error);
      addLog(activityProcessId, `✗ Error: ${error.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(error.message || 'Failed to process file. Please check the file format and try again.');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const getSelectedRegionCount = () => {
    return Object.values(formData.regions).filter(Boolean).length;
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.csv')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select an Excel (.xlsx) or CSV file');
        setSelectedFile(null);
      }
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="PO Distribution" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          PO DISTRIBUTION
        </Typography>

      <Grid container spacing={3}>
        {/* Configuration Panel */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            {/* Product Type Selection */}
            <Card sx={{ mb: 3, bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
              <CardContent>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                    Product Type
                  </FormLabel>
                  <RadioGroup
                    value={formData.productType}
                    onChange={handleProductTypeChange}
                    row
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="rugs"
                      control={<Radio sx={{ '&.Mui-checked': { color: '#228B22' } }} />}
                      label="Rugs"
                    />
                    <FormControlLabel
                      value="nonRugs"
                      control={<Radio sx={{ '&.Mui-checked': { color: '#228B22' } }} />}
                      label="Non Rugs"
                    />
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>

            {/* Distribution Options */}
            <Card sx={{ mb: 3, bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
              <CardContent>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                    Distribution Options
                  </FormLabel>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.evenDistribution}
                        onChange={handleEvenDistributionChange}
                        sx={{ '&.Mui-checked': { color: '#228B22' } }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Even Distribution
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Units will be rounded to even numbers and distributed in increments of 2
                        </Typography>
                      </Box>
                    }
                    sx={{ mt: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.evenDistribution4}
                        onChange={handleEvenDistribution4Change}
                        sx={{ '&.Mui-checked': { color: '#228B22' } }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          Minimum 4 Per Location
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Each location receiving units will get a minimum of 4. If total is less than 4, it will be set to 4
                        </Typography>
                      </Box>
                    }
                    sx={{ mt: 1 }}
                  />
                </FormControl>
              </CardContent>
            </Card>

            {/* Region Selection */}
            <Card sx={{ mb: 3, bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
              <CardContent>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, '&.Mui-focused': { color: theme.palette.text.primary }, mb: 2 }}>
                    Regional Distribution ({getSelectedRegionCount()} selected)
                  </FormLabel>
                  <Grid container spacing={2}>
                    {Object.entries(regionInfo).map(([region, info]) => (
                      <Grid item xs={12} sm={6} md={4} key={region}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.regions[region]}
                              onChange={() => handleRegionChange(region)}
                              sx={{ '&.Mui-checked': { color: '#228B22' } }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {region} - {info.locations.join(', ')}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {formData.regions.WC && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.splitWC}
                          onChange={(e) => setFormData(prev => ({ ...prev, splitWC: e.target.checked }))}
                          sx={{ '&.Mui-checked': { color: '#228B22' }, ml: 2 }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            Split L60 / L65
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            Treat L60 and L65 as separate regions in the distribution calculation (adds L60 TO ORDER and L65 TO ORDER columns)
                          </Typography>
                        </Box>
                      }
                      sx={{ mt: 1 }}
                    />
                  )}
                </FormControl>
              </CardContent>
            </Card>

            {/* File Upload & Processing */}
            <Card sx={{ mb: 3, bgcolor: selectedFile ? (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa') : 'transparent' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <FaFileUpload color="#228B22" size={24} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Import File (Excel/CSV)
                  </Typography>
                  {selectedFile && (
                    <MdCheckCircle color="#228B22" />
                  )}
                </Box>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".xlsx,.csv"
                  style={{ display: 'none' }}
                />

                {/* Drag and Drop Zone */}
                <Box
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    border: `2px dashed ${isDragging ? '#228B22' : selectedFile ? '#4CAF50' : theme.palette.divider}`,
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragging
                      ? (isDarkMode ? 'rgba(34, 139, 34, 0.15)' : 'rgba(34, 139, 34, 0.06)')
                      : selectedFile
                        ? (isDarkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                        : 'transparent',
                    transition: 'all 0.3s ease',
                    mb: 2,
                    '&:hover': {
                      borderColor: selectedFile ? '#4CAF50' : '#228B22',
                      bgcolor: selectedFile
                        ? (isDarkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.08)')
                        : (isDarkMode ? 'rgba(34, 139, 34, 0.1)' : 'rgba(34, 139, 34, 0.03)'),
                    },
                  }}
                >
                  {selectedFile ? (
                    <>
                      <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        1 file selected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to replace
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CloudUpload sx={{ fontSize: 48, color: isDragging ? '#228B22' : theme.palette.text.secondary, mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {isDragging ? 'Drop file here' : 'Drag & drop file here or click to select'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excel (.xlsx) or CSV files only
                      </Typography>
                    </>
                  )}
                </Box>

                {selectedFile && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mt: 1.5,
                      p: 1,
                      bgcolor: 'background.paper',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      <Description sx={{ color: '#228B22', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                        ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} sx={{ color: theme.palette.error.main }}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleDownloadSample}
                    disabled={loading}
                    startIcon={<GetApp sx={{ fontSize: '18px !important' }} />}
                    sx={{
                      borderColor: alpha(theme.palette.text.primary, 0.2),
                      color: alpha(theme.palette.text.primary, 0.7),
                      bgcolor: 'transparent',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2.5,
                      py: 0.8,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: alpha(theme.palette.text.primary, 0.4),
                        color: theme.palette.text.primary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05),
                      },
                      '&:active': {
                        transform: 'scale(0.98)',
                      },
                    }}
                  >
                    Download Template
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Process Button */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleProcessFile}
                disabled={loading || !selectedFile || !formData.productType || getSelectedRegionCount() === 0}
                sx={{
                  bgcolor: '#228B22',
                  '&:hover': { bgcolor: '#1B6B1B' },
                  px: 4
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  'Process & Download'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Status & Instructions Panel */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Status & Instructions
            </Typography>

            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Progress */}
            {loading && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Processing file... {progress > 0 ? `${Math.round(progress)}%` : ''}
                </Typography>
                <LinearProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} />
              </Box>
            )}

            {/* Instructions */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              How it works:
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6, mb: 3 }}>
              1. Select product type (Rugs or Non Rugs)<br />
              2. Enable even distribution if needed<br />
              3. Select target regions for distribution<br />
              4. Upload Excel/CSV file with PO data<br />
              5. Process and download results<br /><br />
              <strong>Required Columns:</strong><br />
              • Rug ID / Vendor ID<br />
              • Units To Order<br />
              • FUTQ (Future Quantities)
            </Typography>

          </Paper>

          {/* Cross-link to the warehouse-aware sibling so users discover the
              per-warehouse split version without hunting through the menu. */}
          <RelatedToolsBar
            tools={PO_SIBLING_TOOLS}
            title="Need warehouse-level splitting?"
            subtitle="Sister tool for per-warehouse allocation"
            layout="stack"
          />
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default PODistribution;